import * as React from "react";
import S from "./Header.styled";
import useHeaderData from "./useHeaderData";
import { getBgColor, toGatsbyImage } from "@/src/helpers";
import ArrowDown from "@/assets/navbar-arrow-down.svg";
import MenuArrow from "@/assets/arrow-down.svg";
import Search from "./Search";
import Button from "@/components/global-components/Button";

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = React.useState<boolean>(false);
  const {
    ctaLinks,
    logo,
    aboutSdf,
    aboutDeafAndSignLanguage,
    radOchStod,
    verksamhet,
    showCtaBlock,
    ctaSubMenu,
  } = useHeaderData();
  const imageData = toGatsbyImage(logo);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [submenuIndex, setSubmenuIndex] = React.useState(-1);
  const [submenuIndexMobile, setSubmenuIndexMobile] = React.useState(-1);
  const [subSubMenuIndex, setSubSubMenuIndex] = React.useState(-1);

  // check scroll position
  React.useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleArrow = (index: number) => {
    submenuIndexMobile === index
      ? setSubmenuIndexMobile(-1)
      : setSubmenuIndexMobile(index);
  };

  const handleSubArrow = (index: number) => {
    subSubMenuIndex === index
      ? setSubSubMenuIndex(-1)
      : setSubSubMenuIndex(index);
  };

  const renderIntressepolitik = () => {
    return (
      <S.SubLinksContainer className="bg-color">
        <S.SubLinksWrapper>
          {aboutSdf.intressepolitikSubMenu.subLink && (
            <S.SubLinkTitleWrapper>
              <S.SubLinkTitle
                to={aboutSdf.intressepolitikSubMenu.subLink.url}
                target={aboutSdf.intressepolitikSubMenu.subLink.target}
              >
                {aboutSdf.intressepolitikSubMenu.subLink.title}
              </S.SubLinkTitle>
              {aboutSdf.intressepolitikSubMenu.subSubLinks && (
                <S.ArrowWrapper
                  $subIndex={subSubMenuIndex === 4}
                  onClick={() => handleSubArrow(4)}
                >
                  <ArrowDown />
                </S.ArrowWrapper>
              )}
            </S.SubLinkTitleWrapper>
          )}
          {aboutSdf.intressepolitikSubMenu.subSubLinks?.length > 0 && (
            <S.SubLinks $showSubSub={subSubMenuIndex === 4}>
              {aboutSdf.intressepolitikSubMenu.subSubLinks?.map(
                ({ subSubLink }, index) => {
                  return (
                    <li key={index}>
                      <S.SubLink to={subSubLink.url} target={subSubLink.target}>
                        {subSubLink.title}
                      </S.SubLink>
                    </li>
                  );
                }
              )}
            </S.SubLinks>
          )}
        </S.SubLinksWrapper>
      </S.SubLinksContainer>
    );
  };

  const renderAboutSdf = () => {
    const renderSubLinks = aboutSdf.subLinks?.map(
      ({ subLink, subSubLinks }, index) => {
        return (
          <S.SubLinksWrapper key={index}>
            {subLink && (
              <S.SubLinkTitleWrapper>
                <S.SubLinkTitle to={subLink.url} target={subLink.target}>
                  {subLink.title}
                </S.SubLinkTitle>
                {subSubLinks && (
                  <S.ArrowWrapper
                    $subIndex={subSubMenuIndex === index}
                    onClick={() => handleSubArrow(index)}
                  >
                    <ArrowDown />
                  </S.ArrowWrapper>
                )}
              </S.SubLinkTitleWrapper>
            )}
            {subSubLinks?.length > 0 && (
              <S.SubLinks $showSubSub={subSubMenuIndex === index}>
                {subSubLinks?.map(({ subSubLink }, index) => {
                  return (
                    <li key={index}>
                      <S.SubLink to={subSubLink.url} target={subSubLink.target}>
                        {subSubLink.title}
                      </S.SubLink>
                    </li>
                  );
                })}
              </S.SubLinks>
            )}
          </S.SubLinksWrapper>
        );
      }
    );

    return (
      <React.Fragment>
        <S.LinkWrapper
          onMouseEnter={() => setSubmenuIndex(0)}
          $bgColor={getBgColor("light", aboutSdf.backgroundColor)}
          $subIndex={submenuIndexMobile === 0}
        >
          <S.Link
            activeClassName="active"
            to={aboutSdf.navbarLink.url}
            target={aboutSdf.navbarLink.target}
            $subIndex={submenuIndex === 0}
            $subMenuOpen={submenuIndex === 0}
          >
            {aboutSdf.navbarLink.title}
          </S.Link>

          {aboutSdf.showSubLinks === "yes" && (
            <S.ArrowWrapper
              aria-label="öppna Om SDF meny"
              onClick={() => handleArrow(0)}
              $subIndex={submenuIndexMobile === 0}
            >
              <ArrowDown />
            </S.ArrowWrapper>
          )}
        </S.LinkWrapper>

        {submenuIndexMobile === 0 && (
          <S.MobileSubMenuOverlay
            $subIndex={submenuIndexMobile === 0}
            $cta={showCtaBlock}
            $bgColor={getBgColor("light", aboutSdf.backgroundColor)}
          >
            {renderSubLinks}
            {renderIntressepolitik()}
          </S.MobileSubMenuOverlay>
        )}
        {submenuIndex === 0 && (
          <>
            <S.SubMenuOverlay
              $subIndex={submenuIndex === 0}
              $cta={showCtaBlock}
              $bgColor={getBgColor("light", aboutSdf.backgroundColor)}
            >
              <S.CTASubMenu>
                {ctaSubMenu.ctaText && (
                  <S.CTAText>{ctaSubMenu.ctaText}</S.CTAText>
                )}
                {ctaSubMenu.ctaLink && (
                  <Button
                    link={ctaSubMenu.ctaLink}
                    type="textLink"
                    arrow
                    color="light"
                  />
                )}
              </S.CTASubMenu>
              <S.SubLinksContainer className="first-container">
                {renderSubLinks}
              </S.SubLinksContainer>
              {renderIntressepolitik()}
            </S.SubMenuOverlay>
            <S.Backdrop onMouseEnter={() => setSubmenuIndex(-1)} />
          </>
        )}
      </React.Fragment>
    );
  };

  const renderVerksamhet = () => {
    const renderSubLinks = verksamhet.subLinks?.map(
      ({ subLink, subSubLinks }, index) => {
        return (
          <S.SubLinksWrapper key={index}>
            {subLink && (
              <S.SubLinkTitleWrapper>
                <S.SubLinkTitle to={subLink.url} target={subLink.target}>
                  {subLink.title}
                </S.SubLinkTitle>
                {subSubLinks && (
                  <S.ArrowWrapper
                    $subIndex={subSubMenuIndex === index}
                    onClick={() => handleSubArrow(index)}
                  >
                    <ArrowDown />
                  </S.ArrowWrapper>
                )}
              </S.SubLinkTitleWrapper>
            )}
            {subSubLinks?.length > 0 && (
              <S.SubLinks $showSubSub={subSubMenuIndex === index}>
                {subSubLinks?.map(({ subSubLink }, index) => {
                  return (
                    <li key={index}>
                      <S.SubLink to={subSubLink.url} target={subSubLink.target}>
                        {subSubLink.title}
                      </S.SubLink>
                    </li>
                  );
                })}
              </S.SubLinks>
            )}
          </S.SubLinksWrapper>
        );
      }
    );

    return (
      <React.Fragment>
        <S.LinkWrapper
          onMouseEnter={() => setSubmenuIndex(1)}
          $bgColor={getBgColor("light", verksamhet.backgroundColor)}
          $subIndex={submenuIndexMobile === 1}
        >
          <S.Link
            activeClassName="active"
            to={verksamhet.navbarLink.url}
            target={verksamhet.navbarLink.target}
            $subIndex={submenuIndex === 1}
            $subMenuOpen={submenuIndex === 1}
          >
            {verksamhet.navbarLink.title}
          </S.Link>

          {verksamhet.showSubLinks === "yes" && (
            <S.ArrowWrapper
              aria-label="öppna Verksamhet meny"
              onClick={() => handleArrow(1)}
              $subIndex={submenuIndexMobile === 1}
            >
              <ArrowDown />
            </S.ArrowWrapper>
          )}
        </S.LinkWrapper>

        {submenuIndexMobile === 1 && (
          <S.MobileSubMenuOverlay
            $subIndex={submenuIndexMobile === 1}
            $cta={showCtaBlock}
            $bgColor={getBgColor("light", verksamhet.backgroundColor)}
          >
            {renderSubLinks}
          </S.MobileSubMenuOverlay>
        )}
        {submenuIndex === 1 && (
          <>
            <S.SubMenuOverlay
              $subIndex={submenuIndex === 1}
              $cta={showCtaBlock}
              $bgColor={getBgColor("light", verksamhet.backgroundColor)}
            >
              <S.CTASubMenu>
                {ctaSubMenu.ctaText && (
                  <S.CTAText>{ctaSubMenu.ctaText}</S.CTAText>
                )}
                {ctaSubMenu.ctaLink && (
                  <Button
                    link={ctaSubMenu.ctaLink}
                    type="textLink"
                    arrow
                    color="light"
                  />
                )}
              </S.CTASubMenu>
              <S.SubLinksContainer>{renderSubLinks}</S.SubLinksContainer>
            </S.SubMenuOverlay>
            <S.Backdrop onMouseEnter={() => setSubmenuIndex(-1)} />
          </>
        )}
      </React.Fragment>
    );
  };

  const renderRadOchStod = () => {
    const renderSubLinks = radOchStod.subLinks?.map(
      ({ subLink, subSubLinks }, index) => {
        return (
          <S.SubLinksWrapper key={index}>
            {subLink && (
              <S.SubLinkTitleWrapper>
                <S.SubLinkTitle to={subLink.url} target={subLink.target}>
                  {subLink.title}
                </S.SubLinkTitle>
                {subSubLinks && (
                  <S.ArrowWrapper
                    $subIndex={subSubMenuIndex === 2}
                    onClick={() => handleSubArrow(2)}
                  >
                    <ArrowDown />
                  </S.ArrowWrapper>
                )}
              </S.SubLinkTitleWrapper>
            )}
            {subSubLinks?.length > 0 && (
              <S.SubLinks $showSubSub={subSubMenuIndex === 2}>
                {subSubLinks?.map(({ subSubLink }, index) => {
                  return (
                    <li key={index}>
                      <S.SubLink to={subSubLink.url} target={subSubLink.target}>
                        {subSubLink.title}
                      </S.SubLink>
                    </li>
                  );
                })}
              </S.SubLinks>
            )}
          </S.SubLinksWrapper>
        );
      }
    );

    return (
      <React.Fragment>
        <S.LinkWrapper
          onMouseEnter={() => setSubmenuIndex(2)}
          $bgColor={getBgColor("light", radOchStod.backgroundColor)}
          $subIndex={submenuIndexMobile === 2}
        >
          <S.Link
            activeClassName="active"
            to={radOchStod.navbarLink.url}
            target={radOchStod.navbarLink.target}
            $subIndex={submenuIndex === 2}
            $subMenuOpen={submenuIndex === 2}
          >
            {radOchStod.navbarLink.title}
          </S.Link>

          {radOchStod.showSubLinks === "yes" && (
            <S.ArrowWrapper
              aria-label="öppna Råd och stöd meny"
              onClick={() => handleArrow(2)}
              $subIndex={submenuIndexMobile === 2}
            >
              <ArrowDown />
            </S.ArrowWrapper>
          )}
        </S.LinkWrapper>

        {submenuIndexMobile === 2 && (
          <S.MobileSubMenuOverlay
            $subIndex={submenuIndexMobile === 2}
            $cta={showCtaBlock}
            $bgColor={getBgColor("light", radOchStod.backgroundColor)}
          >
            {renderSubLinks}
          </S.MobileSubMenuOverlay>
        )}
        {submenuIndex === 2 && (
          <>
            <S.SubMenuOverlay
              $subIndex={submenuIndex === 2}
              $cta={showCtaBlock}
              $bgColor={getBgColor("light", radOchStod.backgroundColor)}
            >
              <S.CTASubMenu>
                {ctaSubMenu.ctaText && (
                  <S.CTAText>{ctaSubMenu.ctaText}</S.CTAText>
                )}
                {ctaSubMenu.ctaLink && (
                  <Button
                    link={ctaSubMenu.ctaLink}
                    type="textLink"
                    arrow
                    color="light"
                  />
                )}
              </S.CTASubMenu>
              <S.SubLinksContainer>{renderSubLinks}</S.SubLinksContainer>
            </S.SubMenuOverlay>
            <S.Backdrop onMouseEnter={() => setSubmenuIndex(-1)} />
          </>
        )}
      </React.Fragment>
    );
  };

  const renderAboutDeafAndSignLanguage = () => {
    const renderSubLinks = aboutDeafAndSignLanguage.subLinks?.map(
      ({ subLink, subSubLinks }, index) => {
        return (
          <S.SubLinksWrapper key={index}>
            {subLink && (
              <S.SubLinkTitleWrapper>
                <S.SubLinkTitle to={subLink.url} target={subLink.target}>
                  {subLink.title}
                </S.SubLinkTitle>
                {subSubLinks && (
                  <S.ArrowWrapper
                    $subIndex={subSubMenuIndex === 3}
                    onClick={() => handleSubArrow(3)}
                  >
                    <ArrowDown />
                  </S.ArrowWrapper>
                )}
              </S.SubLinkTitleWrapper>
            )}
            {subSubLinks?.length > 0 && (
              <S.SubLinks $showSubSub={subSubMenuIndex === 3}>
                {subSubLinks?.map(({ subSubLink }, index) => {
                  return (
                    <li key={index}>
                      <S.SubLink to={subSubLink.url} target={subSubLink.target}>
                        {subSubLink.title}
                      </S.SubLink>
                    </li>
                  );
                })}
              </S.SubLinks>
            )}
          </S.SubLinksWrapper>
        );
      }
    );

    return (
      <React.Fragment>
        <S.LinkWrapper
          onMouseEnter={() => setSubmenuIndex(3)}
          $bgColor={getBgColor(
            "light",
            aboutDeafAndSignLanguage.backgroundColor
          )}
          $subIndex={submenuIndexMobile === 3}
        >
          <S.Link
            activeClassName="active"
            to={aboutDeafAndSignLanguage.navbarLink.url}
            target={aboutDeafAndSignLanguage.navbarLink.target}
            $subIndex={submenuIndex === 3}
            $subMenuOpen={submenuIndex === 3}
          >
            {aboutDeafAndSignLanguage.navbarLink.title}
          </S.Link>

          {aboutDeafAndSignLanguage.showSubLinks === "yes" && (
            <S.ArrowWrapper
              aria-label="öppna Om döva och teckenspråk meny"
              onClick={() => handleArrow(3)}
              $subIndex={submenuIndexMobile === 3}
            >
              <ArrowDown />
            </S.ArrowWrapper>
          )}
        </S.LinkWrapper>

        {submenuIndexMobile === 3 && (
          <S.MobileSubMenuOverlay
            $subIndex={submenuIndexMobile === 3}
            $cta={showCtaBlock}
            $bgColor={getBgColor(
              "light",
              aboutDeafAndSignLanguage.backgroundColor
            )}
          >
            {renderSubLinks}
          </S.MobileSubMenuOverlay>
        )}
        {submenuIndex === 3 && (
          <>
            <S.SubMenuOverlay
              $subIndex={submenuIndex === 3}
              $cta={showCtaBlock}
              $bgColor={getBgColor(
                "light",
                aboutDeafAndSignLanguage.backgroundColor
              )}
            >
              <S.CTASubMenu>
                {ctaSubMenu.ctaText && (
                  <S.CTAText>{ctaSubMenu.ctaText}</S.CTAText>
                )}
                {ctaSubMenu.ctaLink && (
                  <Button
                    link={ctaSubMenu.ctaLink}
                    type="textLink"
                    arrow
                    color="light"
                  />
                )}
              </S.CTASubMenu>
              <S.SubLinksContainer>{renderSubLinks}</S.SubLinksContainer>
            </S.SubMenuOverlay>
            <S.Backdrop onMouseEnter={() => setSubmenuIndex(-1)} />
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <S.Header $scrolled={isScrolled}>
      {showCtaBlock === "yes" && (
        <S.CTABlockDesktop>
          <S.CTALinksWrapper className="arrow-no-transform">
            {ctaLinks?.map(({ ctaLink }, index) => {
              return (
                <Button
                  key={index}
                  link={ctaLink}
                  type="smallTextLink"
                  arrow
                  color="light"
                />
              );
            })}
          </S.CTALinksWrapper>
        </S.CTABlockDesktop>
      )}
      <S.NavBar>
        <S.LogoWrapper to="/" aria-label="Stockholms DF logga">
          {imageData && <S.Logo image={imageData} alt={logo.altText || ""} />}
        </S.LogoWrapper>
        <S.NavWrapperDesktop onMouseLeave={() => setSubmenuIndex(-1)}>
          {renderAboutSdf()}
          {renderVerksamhet()}
          {renderRadOchStod()}
          {renderAboutDeafAndSignLanguage()}
          <S.SearchBtn onMouseEnter={() => setSubmenuIndex(-1)}>
            <Search />
          </S.SearchBtn>
        </S.NavWrapperDesktop>

        <S.MenuBtn onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
          Meny
          <MenuArrow />
        </S.MenuBtn>
      </S.NavBar>
      {isOpen && (
        <S.MobileMenu>
          <S.NavWrapperMobile>
            {renderAboutSdf()}
            {renderVerksamhet()}
            {renderRadOchStod()}
            {renderAboutDeafAndSignLanguage()}
          </S.NavWrapperMobile>
          <Search />
          {showCtaBlock === "yes" && (
            <S.CTABlockMobile>
              <S.CTALinksWrapper>
                {ctaLinks?.map(({ ctaLink }, index) => {
                  return (
                    <Button
                      key={index}
                      link={ctaLink}
                      type="textLink"
                      arrow
                      color="light"
                    />
                  );
                })}
              </S.CTALinksWrapper>
            </S.CTABlockMobile>
          )}
          <S.CTASubMenu>
            {ctaSubMenu.ctaText && <S.CTAText>{ctaSubMenu.ctaText}</S.CTAText>}
            {ctaSubMenu.ctaLink && (
              <Button
                link={ctaSubMenu.ctaLink}
                type="textLink"
                arrow
                color="light"
              />
            )}
          </S.CTASubMenu>
        </S.MobileMenu>
      )}
    </S.Header>
  );
};
export default Header;
